/**
 * Capacity: load against the hours a vendor actually has.
 *
 * The Lines tab answers "how many hours do these orders need". It cannot answer "is this vendor
 * overloaded", because nothing held the other half — not the portal and not the workbook it replaces,
 * whose only summary cells total the load as well. This tab is that half.
 *
 * The hours are typed as their DRIVERS — workers x hours a day x working days x efficiency — rather
 * than as a total, because a total is a figure nobody can argue with six months later. A month that
 * does not fit the formula takes a flat override instead, and the formula stays visible beside it.
 *
 * A vendor with no hours entered shows a blank load, never 0% and never 100%. An unanswered question
 * has to look different from an answer, or the screen quietly invents a capacity nobody stated.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, AlertTriangle, Pencil, Check, X, CalendarDays } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import { BACKEND_URL } from './backendConfig';

const API = `${BACKEND_URL}/api/production/capacity/months`;

/* Typed in the row, the same as the Lines tab. `availableHoursManual` is last because it overrides
   everything to its left and reads better after what it replaces. */
// labelKey / hintKey are dictionary keys (src/locales/xCapacity.js), read through t() where drawn.
const EDITABLE = [
  { key: 'workers', labelKey: 'xCapacity.cm.workers', w: 84, hintKey: 'xCapacity.cm.hintPeople' },
  { key: 'hoursPerDay', labelKey: 'xCapacity.cm.hoursPerDay', w: 92, hintKey: 'xCapacity.cm.hintEg8' },
  { key: 'workDays', labelKey: 'xCapacity.cm.workDays', w: 96, hintKey: 'xCapacity.cm.hintInMonth' },
  { key: 'efficiencyPct', labelKey: 'xCapacity.cm.efficiency', w: 100, hintKey: 'xCapacity.cm.hintAsIs' },
  { key: 'availableHoursManual', labelKey: 'xCapacity.cm.overrideHours', w: 118, hintKey: 'xCapacity.cm.hintInsteadFormula' },
  { key: 'note', labelKey: 'xCapacity.cm.note', w: 200, hintKey: '' },
];
const EDITABLE_KEYS = EDITABLE.map((c) => c.key);

const n0 = (v) => (v === null || v === undefined || v === '' ? '—' : Number(v).toLocaleString('en-GB', { maximumFractionDigits: 0 }));
const n1 = (v) => (v === null || v === undefined || v === '' ? '—' : Number(v).toLocaleString('en-GB', { maximumFractionDigits: 1 }));
const thisMonth = () => new Date().toISOString().slice(0, 7);

/* Where the bands sit is a judgement, so it is in one place with its reasoning rather than sprinkled
   through the markup: at 100% every available hour is already committed, and a plan that lands exactly
   on the line has no room for a late fabric or a failed inspection. 85% is the warning. */
const loadTone = (pct) => {
  if (pct === null || pct === undefined) return '';
  if (pct > 100) return 'cm-over';
  if (pct >= 85) return 'cm-tight';
  return 'cm-ok';
};

export default function CapacityMonths({ mayEdit }) {
  const { t } = useTranslation();
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fromThis, setFromThis] = useState(true);
  const [vendor, setVendor] = useState('');
  const [vendors, setVendors] = useState([]);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(API, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || i18n.t('xCapacity.loadFailed', { status: res.status }));
      setMonths(data.months || []);
      setVendors(data.vendors || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const cut = thisMonth();
    return months.filter((m) => (!fromThis || m.ym >= cut) && (!vendor || m.vendorCode === vendor));
  }, [months, fromThis, vendor]);

  /* The strip describes the rows on screen, and it counts the months with NO capacity separately
     rather than folding them into a total that would read as "we have this much". */
  const totals = useMemo(() => rows.reduce((a, m) => ({
    remaining: a.remaining + m.hoursRemaining,
    available: a.available + (m.availableHours || 0),
    unanswered: a.unanswered + (m.availableHours === null ? 1 : 0),
    over: a.over + (m.loadPct !== null && m.loadPct > 100 ? 1 : 0),
  }), { remaining: 0, available: 0, unanswered: 0, over: 0 }), [rows]);

  const startEdit = (m) => {
    setEditError('');
    setEdit({
      key: `${m.vendorCode}|${m.ym}`,
      form: Object.fromEntries(EDITABLE_KEYS.map((k) => [
        k,
        /* Working days open on the Mon-Sat count when the month is empty. Offered, not stored: until
           somebody presses save this month still has no capacity at all. */
        k === 'workDays' && m.workDays === null && m.workDaysSuggested !== null
          ? String(m.workDaysSuggested)
          : (m[k] === null || m[k] === undefined ? '' : String(m[k])),
      ])),
    });
  };
  const setField = (k) => (e) => setEdit((p) => ({ ...p, form: { ...p.form, [k]: e.target.value } }));

  const saveEdit = async () => {
    if (!edit || saving) return;
    setSaving(true); setEditError('');
    const [vendorCode, ym] = edit.key.split('|');
    try {
      const res = await fetch(`${API}/${encodeURIComponent(vendorCode)}/${ym}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(edit.form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('xCapacity.saveFailed', { status: res.status }));
      setMonths((prev) => prev.map((m) => (m.vendorCode === vendorCode && m.ym === ym ? data.month : m)));
      setEdit(null);
    } catch (err) {
      setEditError(err.message);
    } finally {
      setSaving(false);
    }
  };
  const editKeys = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); saveEdit(); }
    if (e.key === 'Escape') { e.preventDefault(); setEdit(null); setEditError(''); }
  };

  return (
    <>
      {!!error && <div className="sp-error"><AlertTriangle size={14} /> {error}</div>}

      <div className="sp-stat-row cap-stats">
        <div className="sp-stat">
          <div className="sp-stat-value">{n0(totals.remaining)}</div>
          <div className="sp-stat-label">{t('xCapacity.cm.hoursNeeded')}</div>
          <div className="sp-stat-sub">{t('xCapacity.cm.hoursNeededSub')}</div>
        </div>
        <div className="sp-stat">
          <div className="sp-stat-value">{n0(totals.available)}</div>
          <div className="sp-stat-label">{t('xCapacity.cm.hoursAvailable')}</div>
          <div className="sp-stat-sub">
            {totals.unanswered
              ? t('xCapacity.cm.notAnswered', { count: totals.unanswered })
              : t('xCapacity.cm.allAnswered')}
          </div>
        </div>
        <div className={`sp-stat ${totals.over ? 'warn' : 'ok'}`}>
          <div className="sp-stat-value">{totals.over ? n0(totals.over) : t('xCapacity.cm.none')}</div>
          <div className="sp-stat-label">{t('xCapacity.cm.monthsOver')}</div>
          <div className="sp-stat-sub">{t('xCapacity.cm.monthsOverSub')}</div>
        </div>
      </div>

      <div className="cap-bar">
        <button type="button" className={`sp-btn ${fromThis ? 'cap-on' : ''}`} onClick={() => setFromThis((v) => !v)}>
          <CalendarDays size={13} /> {fromThis ? t('xCapacity.cm.fromThisMonth') : t('xCapacity.cm.allMonths')}
        </button>
        <select value={vendor} onChange={(e) => setVendor(e.target.value)}>
          <option value="">{t('xCapacity.allVendors')}</option>
          {vendors.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <div className="cap-bar-right">
          <button type="button" className="sp-btn" onClick={load} disabled={loading}>
            <RefreshCw size={13} /> {loading ? t('xCapacity.loading') : t('xCapacity.reload')}
          </button>
        </div>
      </div>

      {!!editError && <div className="sp-error"><AlertTriangle size={14} /> {editError}</div>}

      <div className="cap-scroll">
        <table className="cap-table cm-table">
          <thead>
            <tr>
              <th>{t('xCapacity.cm.colMonth')}</th>
              <th>{t('xCapacity.cm.colVendor')}</th>
              <th className="cap-num">{t('xCapacity.cm.colLines')}</th>
              <th className="cap-num">{t('xCapacity.cm.colQty')}</th>
              <th className="cap-num">{t('xCapacity.cm.colHoursNeeded')}</th>
              {EDITABLE.map((c) => <th key={c.key} className={c.key === 'note' ? '' : 'cap-num'}>{t(c.labelKey)}</th>)}
              <th className="cap-num">{t('xCapacity.cm.colAvailable')}</th>
              <th className="cap-num">{t('xCapacity.cm.colLoad')}</th>
              <th className="cap-num">{t('xCapacity.cm.colOverBy')}</th>
              <th className="cap-act">{t('xCapacity.colAction')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => {
              const key = `${m.vendorCode}|${m.ym}`;
              const editingThis = edit && edit.key === key;
              return (
                <tr key={key} className={editingThis ? 'cap-editing' : ''}>
                  <td>{m.ym}</td>
                  <td>{m.vendorCode}</td>
                  <td className="cap-num" title={t('xCapacity.stillOpen', { n: m.linesOpen })}>{n0(m.linesTotal)}</td>
                  <td className="cap-num">{n0(m.quantity)}</td>
                  <td className="cap-num">{n0(m.hoursRemaining)}</td>
                  {EDITABLE.map((c) => (editingThis ? (
                    <td key={c.key} className={`cap-cell-edit ${c.key === 'note' ? '' : 'cap-num'}`}>
                      <input
                        type={c.key === 'note' ? 'text' : 'number'}
                        step={c.key === 'note' ? undefined : 'any'}
                        placeholder={c.hintKey ? t(c.hintKey) : ''}
                        value={edit.form[c.key]}
                        onChange={setField(c.key)}
                        onKeyDown={editKeys}
                        /* eslint-disable-next-line jsx-a11y/no-autofocus */
                        autoFocus={c.key === 'workers'}
                      />
                    </td>
                  ) : (
                    <td key={c.key} className={c.key === 'note' ? '' : 'cap-num'} title={c.key === 'note' ? (m.note || '') : undefined}>
                      {c.key === 'note' ? (m.note || '—') : n1(m[c.key])}
                    </td>
                  )))}
                  <td
                    className="cap-num"
                    title={m.availableHoursManual !== null && m.availableHoursFormula !== null
                      ? t('xCapacity.cm.overrideTitle', { n: n0(m.availableHoursFormula) })
                      : undefined}
                  >
                    {m.availableHours === null
                      ? <span className="cm-unset" title={t('xCapacity.cm.notSetTitle')}>{t('xCapacity.cm.notSet')}</span>
                      : <>{n0(m.availableHours)}{m.availableHoursManual !== null && <em className="cm-ovr">{t('xCapacity.cm.ovrShort')}</em>}</>}
                  </td>
                  <td className={`cap-num ${loadTone(m.loadPct)}`}>
                    {m.loadPct === null ? '—' : `${n1(m.loadPct)}%`}
                  </td>
                  <td className={`cap-num ${m.overBy > 0 ? 'cm-over' : ''}`}>
                    {m.overBy === null ? '—' : (m.overBy > 0 ? n0(m.overBy) : '—')}
                  </td>
                  <td className="cap-act">
                    {editingThis ? (
                      <span className="cap-rowbtns">
                        <button type="button" className="sp-btn tiny primary" title={t('xCapacity.saveEnter')} disabled={saving} onClick={saveEdit}>
                          <Check size={12} />
                        </button>
                        <button type="button" className="sp-btn tiny" title={t('xCapacity.cancelEsc')} onClick={() => { setEdit(null); setEditError(''); }}>
                          <X size={12} />
                        </button>
                      </span>
                    ) : mayEdit && (
                      <button type="button" className="sp-btn tiny" title={t('xCapacity.cm.editMonthTitle')} onClick={() => startEdit(m)}>
                        <Pencil size={12} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!rows.length && !loading && (
              <tr><td colSpan={13} className="cap-empty">{t('xCapacity.cm.noMonths')}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
